// src/components/MarketResearch/ActionBar.tsx
import React from 'react';
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
  CircularProgress
} from '@mui/material';
import {
  PlayArrowOutlined,
  PrintOutlined,
  GetAppOutlined,
  ShareOutlined
} from '@mui/icons-material';

interface ActionBarProps {
  onGenerate: () => void;
  onPrint: () => void;
  onExport?: () => void;
  loading: boolean;
  hasData: boolean;
}

export const ActionBar: React.FC<ActionBarProps> = ({
  onGenerate,
  onPrint,
  onExport,
  loading,
  hasData
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        mt: 2
      }}
    >
      {/* Primary Actions */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PlayArrowOutlined />}
          onClick={onGenerate}
          disabled={loading}
          sx={{
            px: 3,
            py: 1.5,
            fontWeight: 600,
            borderRadius: 2,
            textTransform: 'none',
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            '&:hover': {
              boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
              transform: 'translateY(-1px)'
            },
            '&:disabled': {
              opacity: 0.7
            }
          }}
        >
          {loading ? 'Analyzing Market...' : hasData ? 'Regenerate Analysis' : 'Generate Market Research'}
        </Button>
      </Box>

      {/* Secondary Actions */}
      {hasData && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Print Report" arrow>
            <IconButton
              onClick={onPrint}
              sx={{
                backgroundColor: alpha(theme.palette.grey[500], 0.1),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.grey[500], 0.2),
                  transform: 'translateY(-1px)'
                }
              }}
            >
              <PrintOutlined />
            </IconButton>
          </Tooltip>

          {onExport && (
            <Tooltip title="Export PDF" arrow>
              <IconButton
                onClick={onExport}
                sx={{
                  backgroundColor: alpha(theme.palette.grey[500], 0.1),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.grey[500], 0.2),
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                <GetAppOutlined />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Share Report" arrow>
            <IconButton
              sx={{
                backgroundColor: alpha(theme.palette.grey[500], 0.1),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.grey[500], 0.2),
                  transform: 'translateY(-1px)'
                }
              }}
            >
              <ShareOutlined />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};
